import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, Buildings, CheckCircle, Clock, PencilSimple, Eye } from "@phosphor-icons/react";
import { fmtDate, fmtDateTime } from "@/lib/fmt";
import PhoneActions from "@/components/PhoneActions";

const RR_STATUS = {
  pending_price: "Awaiting price",
  priced: "Priced",
  accepted: "Accepted",
  rejected: "Rejected",
  cancelled: "Cancelled",
  converted: "Converted to ride",
};

const EMPTY_FORM = { name: "", email: "", password: "", contact_name: "", phone: "" };

export default function AgencyManagement() {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editAgency, setEditAgency] = useState(null); // agency obj
  const [detailOpen, setDetailOpen] = useState(null); // detail payload
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const fetchAgencies = useCallback(async () => {
    try {
      const { data } = await api.get("/agencies");
      setAgencies(data);
    } catch {}
  }, []);
  useEffect(() => { fetchAgencies(); }, [fetchAgencies]);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email & password required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/agencies", form);
      toast.success("Account created (pre-approved)");
      setForm(EMPTY_FORM);
      setAddOpen(false);
      fetchAgencies();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editAgency) return;
    setLoading(true);
    try {
      const payload = { name: form.name, email: form.email, contact_name: form.contact_name, phone: form.phone };
      if (form.password) payload.password = form.password;
      await api.put(`/agencies/${editAgency.id}`, payload);
      toast.success("Saved");
      setEditAgency(null);
      fetchAgencies();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  const openEdit = (a) => {
    setForm({ name: a.name || "", email: a.email || "", contact_name: a.contact_name || "", phone: a.phone || "", password: "" });
    setEditAgency(a);
  };

  const openDetail = async (a) => {
    try {
      const { data } = await api.get(`/agencies/${a.id}/detail`);
      setDetailOpen(data);
    } catch {
      toast.error("Failed to load detail");
    }
  };

  const approve = async (a) => {
    try { await api.put(`/agencies/${a.id}/approve`); toast.success(`${a.name} approved`); fetchAgencies(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try { await api.delete(`/agencies/${a.id}`); toast.success("Deleted"); fetchAgencies(); }
    catch { toast.error("Failed"); }
  };

  const pending = agencies.filter((a) => a.account_status === "pending");
  const approved = agencies.filter((a) => a.account_status !== "pending");

  return (
    <div data-testid="agency-management" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button data-testid="back-to-admin-btn" variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
              <ArrowLeft size={18} />
            </Button>
            <h1 className="text-lg font-medium text-white flex items-center gap-2">
              <Buildings size={20} className="text-amber-500" /> Agencies &amp; Private Clients
            </h1>
          </div>
          <Button data-testid="add-agency-btn" onClick={() => { setForm(EMPTY_FORM); setAddOpen(true); }} className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-9">
            <Plus size={16} weight="bold" className="mr-1" /> Add Account
          </Button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {pending.length > 0 && (
          <>
            <p className="text-xs text-amber-500 uppercase tracking-wider font-medium mb-2 flex items-center gap-1">
              <Clock size={14} /> Pending Approval ({pending.length})
            </p>
            <div className="space-y-2 mb-6">
              {pending.map((a) => (
                <AgencyCard key={a.id} a={a} onApprove={() => approve(a)} onDelete={() => remove(a)} onEdit={() => openEdit(a)} onDetail={() => openDetail(a)} pending />
              ))}
            </div>
          </>
        )}
        <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-2">Approved ({approved.length})</p>
        <div className="space-y-2">
          {approved.map((a) => (
            <AgencyCard key={a.id} a={a} onEdit={() => openEdit(a)} onDetail={() => openDetail(a)} onDelete={() => remove(a)} />
          ))}
          {approved.length === 0 && pending.length === 0 && (
            <div className="text-center py-10 text-zinc-500 text-sm">No accounts yet</div>
          )}
        </div>
      </main>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={() => setAddOpen(false)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Add Agency / Private Client</DialogTitle>
            <DialogDescription className="text-zinc-500">Account is created pre-approved — ready to log in immediately.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-3" data-testid="add-agency-form">
            <FormField label="Name (agency or client)" required>
              <Input data-testid="add-agency-name" value={form.name} onChange={setField("name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </FormField>
            <FormField label="Contact person (optional)">
              <Input value={form.contact_name} onChange={setField("contact_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </FormField>
            <FormField label="Email" required>
              <Input data-testid="add-agency-email" type="email" value={form.email} onChange={setField("email")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </FormField>
            <FormField label="Phone">
              <Input value={form.phone} onChange={setField("phone")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" placeholder="+33 ..." />
            </FormField>
            <FormField label="Password" required>
              <Input data-testid="add-agency-password" type="password" value={form.password} onChange={setField("password")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </FormField>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
              <Button data-testid="add-agency-submit" type="submit" disabled={loading} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600">
                {loading ? "..." : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editAgency} onOpenChange={() => setEditAgency(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {editAgency?.name}</DialogTitle>
            <DialogDescription className="text-zinc-500">Leave password blank to keep current.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3" data-testid="edit-agency-form">
            <FormField label="Name"><Input data-testid="edit-agency-name" value={form.name} onChange={setField("name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></FormField>
            <FormField label="Contact person"><Input value={form.contact_name} onChange={setField("contact_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></FormField>
            <FormField label="Email">
              <div className="flex gap-2">
                <Input type="email" value={form.email} onChange={setField("email")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10 flex-1" />
                <Button
                  data-testid="edit-agency-test-email"
                  type="button"
                  variant="outline"
                  disabled={!form.email}
                  onClick={async () => {
                    try { await api.post("/admin/email-test", { to: form.email }); toast.success(`Test email sent to ${form.email}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 shrink-0"
                >
                  Send test
                </Button>
              </div>
            </FormField>
            <FormField label="Phone"><Input value={form.phone} onChange={setField("phone")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></FormField>
            <FormField label="New password (optional)"><Input type="password" value={form.password} onChange={setField("password")} placeholder="Leave blank to keep" className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></FormField>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setEditAgency(null)} className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
              <Button data-testid="edit-agency-submit" type="submit" disabled={loading} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600">
                {loading ? "..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailOpen} onOpenChange={() => setDetailOpen(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailOpen && (
            <>
              <DialogHeader>
                <DialogTitle>{detailOpen.agency?.name}</DialogTitle>
                <DialogDescription className="text-zinc-500">
                  {detailOpen.agency?.email} · {detailOpen.agency?.phone || "no phone"}
                </DialogDescription>
              </DialogHeader>

              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-3 mb-2">Requests ({detailOpen.requests?.length || 0})</p>
              {detailOpen.requests?.length === 0 ? <p className="text-sm text-zinc-500">No requests yet</p> : (
                <div className="space-y-1.5">
                  {detailOpen.requests.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 bg-[#1A1A1A] rounded p-2 text-xs">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{r.pickup_location} → {r.dropoff_location}</p>
                        <p className="text-zinc-500">{fmtDate(r.pickup_date)} {r.pickup_time} · {r.client_name}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <Badge className="bg-zinc-700/30 text-zinc-300 border border-zinc-600 text-[10px]">{RR_STATUS[r.status] || r.status}</Badge>
                        {r.admin_price != null && <p className="text-amber-400 mt-1">{r.admin_price} EUR</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-4 mb-2">Rides ({detailOpen.rides?.length || 0})</p>
              {detailOpen.rides?.length === 0 ? <p className="text-sm text-zinc-500">No rides yet</p> : (
                <div className="space-y-1.5">
                  {detailOpen.rides.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 bg-[#1A1A1A] rounded p-2 text-xs">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{r.route_label}</p>
                        <p className="text-zinc-500">{fmtDate(r.pickup_date)} {r.pickup_time}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <Badge className="bg-zinc-700/30 text-zinc-300 border border-zinc-600 text-[10px]">{r.status}</Badge>
                        {r.driver_fare != null && (
                          <p className="mt-1 text-[10px] text-zinc-500 leading-tight">
                            Tarif chauffeur<br/>
                            <span className="text-amber-400 text-xs font-medium">{r.driver_fare} EUR</span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-zinc-400 uppercase tracking-wider">{label}{required && <span className="text-amber-500 ml-1">*</span>}</Label>
      {children}
    </div>
  );
}

function AgencyCard({ a, onApprove, onDelete, onEdit, onDetail, pending }) {
  return (
    <div data-testid={`agency-row-${a.id}`} className={`flex items-center gap-3 rounded-lg p-4 border ${pending ? "bg-amber-500/5 border-amber-500/20" : "bg-[#121212] border-zinc-800"}`}>
      <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
        <Buildings size={18} className="text-amber-500" weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{a.name}</p>
        <p className="text-xs text-zinc-500 truncate">{a.email}</p>
        {a.contact_name && <p className="text-xs text-zinc-500 truncate">Contact: {a.contact_name}</p>}
        {a.phone && <div className="mt-1.5"><PhoneActions phone={a.phone} size="xs" withSms /></div>}
        <p className="text-[10px] text-zinc-600 mt-0.5">Created {fmtDateTime(a.created_at)}</p>
      </div>
      <Button data-testid={`detail-agency-${a.id}`} size="sm" variant="ghost" onClick={onDetail} className="text-zinc-400 hover:text-white hover:bg-zinc-800"><Eye size={16} /></Button>
      <Button data-testid={`edit-agency-${a.id}`} size="sm" variant="ghost" onClick={onEdit} className="text-zinc-400 hover:text-amber-500 hover:bg-zinc-800"><PencilSimple size={16} /></Button>
      {pending && onApprove && (
        <Button data-testid={`approve-agency-${a.id}`} size="sm" onClick={onApprove} className="bg-emerald-600 text-white hover:bg-emerald-700 h-8">
          <CheckCircle size={14} className="mr-1" /> Approve
        </Button>
      )}
      <Button data-testid={`delete-agency-${a.id}`} size="sm" variant="ghost" onClick={onDelete} className="text-red-500/70 hover:text-red-400 hover:bg-red-500/10">
        <Trash size={16} />
      </Button>
    </div>
  );
}
